import type { Ingredient, IngredientAlternative } from '../types';

const isLikelyAlternativePart = (part: string): boolean => {
    const trimmed = part.trim();
    if (!trimmed) return false;
    if (!/[\u0590-\u05FFa-zA-Z]/.test(trimmed)) return false;
    // Ignore fragments from broken numeric fractions like "(3/4 cup)" in the name field.
    if (/^\d+$/.test(trimmed)) return false;
    if (/^\d+\/\d/.test(trimmed) && !/[\u0590-\u05FFa-zA-Z]{2,}/.test(trimmed.replace(/[\d().]/g, ''))) {
        return false;
    }
    return true;
};

/**
 * When the model leaves slash-separated OR options in `name` (e.g. "סוכר לבן/תחליף סוכר"),
 * split them into a primary ingredient plus `alternatives`.
 */
export function splitSlashAlternativesInIngredient(ing: Ingredient): Ingredient {
    const name = ing.name?.trim() ?? '';
    if (!name.includes('/')) return ing;

    const parts = name.split(/\s*\/\s*/).map((part) => part.trim()).filter(Boolean);
    if (parts.length < 2 || !parts.every(isLikelyAlternativePart)) return ing;

    const [primary, ...optionNames] = parts;
    const slashAlternatives: IngredientAlternative[] = optionNames.map((altName) => ({
        name: altName,
        quantity: ing.quantity,
        unit: ing.unit,
        isMeat: ing.isMeat ?? false,
    }));

    const seen = new Set<string>();
    const merged: IngredientAlternative[] = [];
    for (const alt of [...(ing.alternatives ?? []), ...slashAlternatives]) {
        const key = alt.name.trim().toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        merged.push(alt);
    }

    return {
        ...ing,
        name: primary,
        ...(merged.length > 0 ? { alternatives: merged } : {}),
    };
}

export function splitSlashAlternativesInIngredients(ingredients: Ingredient[]): Ingredient[] {
    return ingredients.map(splitSlashAlternativesInIngredient);
}
